/**
 * ADA Fix: Tabs Keyboard Navigation
 * 
 * Issue: Tabs were structured as collapsible buttons with aria-expanded instead
 * of proper tab role with aria-selected. Screen reader users couldn't understand
 * the tab functionality.
 * 
 * Recommendation: Use proper ARIA roles (tablist, tab, tabpanel, aria-selected)
 * and implement W3C ARIA keyboard support for tabs.
 * 
 * Reference: https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 * 
 * Keyboard support:
 * - Arrow Left/Right: Navigate between tabs (circular)
 * - Home: Move to first tab
 * - End: Move to last tab
 * - Tab: Move focus out of tablist
 * - Enter/Space: Activate focused tab (if not already active)
 */

(function() {
  'use strict';

  console.log('🔧 ADA Fix: Initializing tabs keyboard navigation...');

  /**
   * Tab component class
   */
  class AccessibleTabs {
    constructor(tablist) {
      this.tablist = tablist;
      this.tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
      this.tabpanels = [];

      // Find all associated tabpanels
      this.tabs.forEach(tab => {
        const panelId = tab.getAttribute('aria-controls');
        if (panelId) {
          const panel = document.getElementById(panelId);
          if (panel) {
            this.tabpanels.push(panel);
          }
        }
      });

      this.init();
    }

    init() {
      // Check if tabs are inside loess-scroller-tabs component
      this.isLoessComponent = this.tablist.closest('loess-scroller-tabs') !== null;
      
      // Add keyboard event listeners to each tab
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('keydown', this.handleKeyDown.bind(this));
        
        // Only add click listener if NOT inside loess-scroller-tabs
        // (that component handles clicks itself)
        if (!this.isLoessComponent) {
          tab.addEventListener('click', this.handleClick.bind(this));
        }
        
        // Store the index for easy access
        tab.dataset.tabIndex = index;
      });

      console.log(`✅ Initialized accessible tabs: ${this.tabs.length} tabs, ${this.tabpanels.length} panels (Loess component: ${this.isLoessComponent})`);
    }

    handleKeyDown(event) {
      const currentTab = event.currentTarget;
      const currentIndex = parseInt(currentTab.dataset.tabIndex);
      let targetIndex = currentIndex;

      switch(event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          targetIndex = currentIndex === 0 ? this.tabs.length - 1 : currentIndex - 1;
          this.focusTab(targetIndex);
          break;

        case 'ArrowRight':
          event.preventDefault();
          targetIndex = currentIndex === this.tabs.length - 1 ? 0 : currentIndex + 1;
          this.focusTab(targetIndex);
          break;

        case 'Home':
          event.preventDefault();
          this.focusTab(0);
          break;

        case 'End':
          event.preventDefault();
          this.focusTab(this.tabs.length - 1);
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          this.activateTab(currentIndex);
          break;
      }
    }

    handleClick(event) {
      const currentTab = event.currentTarget;
      const currentIndex = parseInt(currentTab.dataset.tabIndex);
      this.activateTab(currentIndex);
    }

    focusTab(index) {
      // Move focus to the target tab
      this.tabs[index].focus();
    }

    activateTab(index) {
      const targetTab = this.tabs[index];
      const targetPanel = this.tabpanels[index];

      // Don't do anything if already active
      if (targetTab.getAttribute('aria-selected') === 'true') {
        return;
      }

      // Deactivate all tabs and hide all panels
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
        // Keep aria-expanded for backward compatibility with existing theme JS
        tab.setAttribute('aria-expanded', 'false');
        tab.removeAttribute('active');
        // Also update parent element if it exists
        if (tab.parentElement && tab.parentElement.tagName === 'LI') {
          tab.parentElement.removeAttribute('active');
        }

        // Hide corresponding panel
        if (this.tabpanels[i]) {
          this.tabpanels[i].setAttribute('hidden', '');
        }
      });

      // Activate the target tab and show its panel
      targetTab.setAttribute('aria-selected', 'true');
      targetTab.setAttribute('tabindex', '0');
      // Keep aria-expanded for backward compatibility with existing theme JS
      targetTab.setAttribute('aria-expanded', 'true');
      targetTab.setAttribute('active', '');
      // Also update parent element if it exists
      if (targetTab.parentElement && targetTab.parentElement.tagName === 'LI') {
        targetTab.parentElement.setAttribute('active', '');
      }

      if (targetPanel) {
        targetPanel.removeAttribute('hidden');
        
        // Remove 'open' attribute if present (from old structure)
        targetPanel.removeAttribute('open');
      }

      console.log(`✅ Activated tab ${index + 1}: "${targetTab.textContent.trim()}"`);

      // Dispatch custom event for any other scripts that need to know about tab changes
      const event = new CustomEvent('tabchange', {
        detail: {
          tab: targetTab,
          panel: targetPanel,
          index: index
        },
        bubbles: true
      });
      this.tablist.dispatchEvent(event);
    }

    /**
     * Public method to activate a tab by index
     */
    selectTab(index) {
      if (index >= 0 && index < this.tabs.length) {
        this.activateTab(index);
      }
    }

    /**
     * Public method to get the currently selected tab
     */
    getSelectedTab() {
      return this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
    }

    /**
     * Public method to get the currently selected tab index
     */
    getSelectedIndex() {
      return this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
    }
  }

  /**
   * Initialize all tab components on the page
   */
  function initializeAllTabs() {
    const tablists = document.querySelectorAll('[role="tablist"]');
    
    if (tablists.length === 0) {
      console.log('ℹ️  No tablists found on this page');
      return [];
    }

    console.log(`🔍 Found ${tablists.length} tablist(s)`);

    const tabInstances = [];
    tablists.forEach((tablist, index) => {
      try {
        const instance = new AccessibleTabs(tablist);
        tabInstances.push(instance);
        
        // Store instance on the element for external access
        tablist.accessibleTabsInstance = instance;
        
      } catch (error) {
        console.error(`❌ Error initializing tablist ${index + 1}:`, error);
      }
    });

    return tabInstances;
  }

  /**
   * Override the existing loess-scroller-tabs behavior if needed
   */
  function overrideExistingTabsBehavior() {
    // Wait for custom elements to be defined
    if (window.customElements && window.customElements.get('loess-scroller-tabs')) {
      console.log('🔄 Enhancing existing loess-scroller-tabs component...');
      
      // The tabs are now properly structured, so we just need to ensure
      // our accessibility enhancements work alongside the existing behavior
      const scrollerTabs = document.querySelectorAll('loess-scroller-tabs');
      
      scrollerTabs.forEach(scroller => {
        // Add a class to indicate it's been enhanced
        scroller.classList.add('tabs-accessibility-enhanced');
      });
    }
  }

  /**
   * Setup mutation observer for dynamically added tabs
   */
  function setupDynamicTabsObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldReinitialize = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if it's a tablist or contains tablists
              if (node.getAttribute('role') === 'tablist' || 
                  (node.querySelector && node.querySelector('[role="tablist"]'))) {
                shouldReinitialize = true;
              }
            }
          });
        }
      });

      if (shouldReinitialize) {
        setTimeout(function() {
          console.log('🔄 Reinitializing tabs due to DOM changes...');
          initializeAllTabs();
        }, 200);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Main initialization function
   */
  function initialize() {
    console.log('🚀 Starting tabs accessibility enhancement...');

    // Initialize all existing tabs
    const tabInstances = initializeAllTabs();

    // Override existing behavior if needed
    overrideExistingTabsBehavior();

    // Setup observer for dynamic content
    setupDynamicTabsObserver();

    console.log('✅ Tabs keyboard navigation initialized');
    console.log(`📊 Total tab components: ${tabInstances.length}`);

    return tabInstances;
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also run after a delay for dynamic content
  setTimeout(initialize, 1000);
  setTimeout(initialize, 2000);

  // Expose for testing and external access
  window.adaTabsKeyboardNav = {
    AccessibleTabs,
    initialize,
    initializeAllTabs
  };

  console.log('🔧 ADA Tabs Keyboard Navigation loaded. Functions available in window.adaTabsKeyboardNav');

})();
